import { z } from 'zod';
import { UserRole, UserStatus } from '../types/user.js';
// ---------------------------------------------------------------------------
// 기본 상수 스키마
// ---------------------------------------------------------------------------
export const UserRoleSchema = z.enum([UserRole.ADMIN, UserRole.OPERATOR, UserRole.VIEWER]);
export const UserStatusSchema = z.enum([UserStatus.PENDING, UserStatus.ACTIVE, UserStatus.DORMANT]);
// ---------------------------------------------------------------------------
// 비밀번호 정책
// ---------------------------------------------------------------------------
const passwordSchema = z
    .string()
    .min(8, '비밀번호는 8자 이상이어야 합니다')
    .max(128)
    .regex(/[A-Z]/, '대문자를 1개 이상 포함해야 합니다')
    .regex(/[0-9]/, '숫자를 1개 이상 포함해야 합니다');
// ---------------------------------------------------------------------------
// 사용자 정보 스키마
// ---------------------------------------------------------------------------
export const UserInfoSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string().min(1).max(100),
    role: UserRoleSchema,
    isActive: z.boolean(),
    status: UserStatusSchema,
    lastLoginAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ---------------------------------------------------------------------------
// 사용자 생성/수정 요청 스키마
// ---------------------------------------------------------------------------
export const CreateUserSchema = z.object({
    email: z.string().email(),
    password: passwordSchema,
    name: z.string().min(1).max(100),
    role: UserRoleSchema,
});
export const UpdateUserSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    role: UserRoleSchema.optional(),
    isActive: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// 회원가입(사용자 앱 공개 가입) 요청 스키마
// 로그인은 username 기반이므로 username 필수. 가입자는 일반 USER 권한.
// ---------------------------------------------------------------------------
export const RegisterUserSchema = z.object({
    username: z
        .string()
        .min(4, '아이디는 4자 이상이어야 합니다')
        .max(30)
        .regex(/^[a-zA-Z0-9_]+$/, '아이디는 영문/숫자/밑줄만 사용할 수 있습니다'),
    password: z.string().min(8, '비밀번호는 8자 이상이어야 합니다').max(128),
    name: z.string().min(1).max(50),
    phone: z.string().min(10, '휴대폰 번호를 정확히 입력해주세요').max(20),
    email: z.string().email().optional(),
    marketingAgreed: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// 관리자 콘솔 계정 신청(공개) 요청 스키마
// 앱 공개가입(RegisterUserSchema)과 달리 ADMIN/OPERATOR 권한을 신청하며,
// 생성 시 status='pending' 으로 저장되어 관리자 승인 전까지 로그인할 수 없다.
// ---------------------------------------------------------------------------
export const RequestAccessSchema = z.object({
    username: z
        .string()
        .min(4, '아이디는 4자 이상이어야 합니다')
        .max(30)
        .regex(/^[a-zA-Z0-9_]+$/, '아이디는 영문/숫자/밑줄만 사용할 수 있습니다'),
    password: z.string().min(8, '비밀번호는 8자 이상이어야 합니다').max(128),
    name: z.string().min(1).max(50),
    phone: z.string().min(10, '휴대폰 번호를 정확히 입력해주세요').max(20),
    email: z.string().email().optional(),
    /** 신청 권한 — 본사 관리자(ADMIN) 또는 지역 담당자(OPERATOR)만 허용 */
    requestedRole: z.enum([UserRole.ADMIN, UserRole.OPERATOR]),
    org: z.string().max(100).optional(),
});
// ---------------------------------------------------------------------------
// 인증 토큰 스키마
// ---------------------------------------------------------------------------
export const AuthTokenSchema = z.object({
    accessToken: z.string().min(1),
    refreshToken: z.string().min(1),
    expiresAt: z.string().datetime(),
    tokenType: z.literal('Bearer'),
});
// ---------------------------------------------------------------------------
// 인증 요청 / 응답 스키마
// ---------------------------------------------------------------------------
export const LoginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const LoginResponseSchema = z.object({
    user: UserInfoSchema,
    token: AuthTokenSchema,
});
export const RefreshTokenRequestSchema = z.object({
    refreshToken: z.string().min(1),
});
export const ChangePasswordRequestSchema = z
    .object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
})
    .refine((data) => data.currentPassword !== data.newPassword, {
    message: '새 비밀번호는 기존 비밀번호와 달라야 합니다',
    path: ['newPassword'],
});
//# sourceMappingURL=user.js.map